import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, CreditCard, Check, X, Search, Save, Trash2, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminStripeManagement = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, user, loading: authLoading } = useAuth();
  
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (user && !user.is_admin) {
      toast.error('Accès réservé à l\'administrateur');
      navigate('/');
      return;
    }

    fetchPractitioners();
  }, [isAuthenticated, authLoading, user, navigate]);

  const fetchPractitioners = async () => {
    try {
      const res = await axios.get(`${API}/admin/stripe-connect/practitioners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPractitioners(res.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (practitionerId) => {
    if (!editValue.trim()) {
      toast.error('Veuillez entrer un ID Stripe Connect');
      return;
    }
    
    if (!editValue.startsWith('acct_')) {
      toast.error('L\'ID doit commencer par "acct_"');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/stripe-connect/practitioners/${practitionerId}`,
        { 
          stripe_connect_account_id: editValue.trim(),
          onboarding_complete: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Compte Stripe associé');
      setEditingId(null);
      setEditValue('');
      fetchPractitioners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (practitionerId) => {
    if (!confirm('Supprimer l\'association Stripe Connect ?')) return;
    
    try {
      await axios.delete(
        `${API}/admin/stripe-connect/practitioners/${practitionerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Association supprimée');
      fetchPractitioners();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filteredPractitioners = practitioners.filter(p => {
    const search = searchTerm.toLowerCase();
    return (
      p.email?.toLowerCase().includes(search) ||
      p.first_name?.toLowerCase().includes(search) ||
      p.last_name?.toLowerCase().includes(search) ||
      p.stripe_connect_account_id?.toLowerCase().includes(search)
    );
  });

  const connectedCount = practitioners.filter(p => p.stripe_connect_onboarding_complete).length;
  const pendingCount = practitioners.filter(p => p.stripe_connect_account_id && !p.stripe_connect_onboarding_complete).length;
  const notConnectedCount = practitioners.filter(p => !p.stripe_connect_account_id).length;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container-app py-8 text-center">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container-app py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-indigo-500" />
                Gestion Stripe Connect
              </h1>
              <p className="text-muted-foreground text-sm">
                Associer les comptes Stripe aux praticiens
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{connectedCount}</div>
            <div className="text-sm text-green-700">Connectés</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
            <div className="text-sm text-amber-700">En cours</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{notConnectedCount}</div>
            <div className="text-sm text-gray-700">Non connectés</div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom, email ou ID Stripe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-sand-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sand-50 border-b border-sand-200">
                <tr>
                  <th className="text-left p-4 font-medium text-sm">Praticien</th>
                  <th className="text-left p-4 font-medium text-sm">Pays</th>
                  <th className="text-left p-4 font-medium text-sm">ID Stripe Connect</th>
                  <th className="text-left p-4 font-medium text-sm">Statut</th>
                  <th className="text-left p-4 font-medium text-sm">PayPal</th>
                  <th className="text-right p-4 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand-200">
                {filteredPractitioners.map(p => (
                  <tr key={p.id} className="hover:bg-sand-50">
                    <td className="p-4">
                      <div className="font-medium">{p.first_name} {p.last_name}</div>
                      <div className="text-sm text-muted-foreground">{p.email}</div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm">{p.country || 'FR'}</span>
                    </td>
                    <td className="p-4">
                      {editingId === p.id ? (
                        <div className="flex gap-2">
                          <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="acct_xxxxx"
                            className="w-48 text-sm"
                          />
                          <Button size="sm" onClick={() => handleSave(p.id)} disabled={saving}>
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditValue(''); }}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {p.stripe_connect_account_id ? (
                            <>
                              <code className="text-xs bg-sand-100 px-2 py-1 rounded">
                                {p.stripe_connect_account_id}
                              </code>
                              <a 
                                href={`https://dashboard.stripe.com/connect/accounts/${p.stripe_connect_account_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-500 hover:text-indigo-600"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </>
                          ) : (
                            <span className="text-muted-foreground text-sm">Non configuré</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      {p.stripe_connect_onboarding_complete ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                          <Check className="w-3 h-3" /> Actif
                        </span>
                      ) : p.stripe_connect_account_id ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
                          En cours
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                          <X className="w-3 h-3" /> Non
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      {p.paypal_email ? (
                        <span className="text-xs text-blue-600">{p.paypal_email}</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        {!p.stripe_connect_account_id ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setEditingId(p.id); setEditValue(''); }}
                          >
                            Associer
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setEditingId(p.id); setEditValue(p.stripe_connect_account_id); }}
                            >
                              Modifier
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => handleRemove(p.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
          <h3 className="font-medium text-indigo-800 mb-2">Comment associer un compte Stripe Connect ?</h3>
          <ol className="text-sm text-indigo-700 space-y-1 list-decimal list-inside">
            <li>Créez le compte Stripe Connect depuis le <a href="https://dashboard.stripe.com/connect/accounts/overview" target="_blank" rel="noopener noreferrer" className="underline">Dashboard Stripe</a></li>
            <li>Copiez l'ID du compte (commence par <code className="bg-indigo-100 px-1 rounded">acct_</code>)</li>
            <li>Cliquez sur "Associer" pour le praticien correspondant</li>
            <li>Collez l'ID et enregistrez</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AdminStripeManagement;
